<template>
    <div
        class="flex w-full flex-col gap-1 font-inter font-normal"
        :class="`ds-text-input-size-${size}`"
    >
        <DsInputLabel
            :id
            :disabled
            :label
            :labelTooltip
            :required
            :size
        >
            <template
                v-if="$slots.label"
                #label
            >
                <slot name="label" />
            </template>
            <template
                v-if="$slots.labelTooltip"
                #labelTooltip
            >
                <slot name="labelTooltip" />
            </template>
        </DsInputLabel>
        <label
            class="ds-text-input"
            :class="[inputWrapperClasses, { 'has-warning': hasWarning }]"
        >
            <div
                class="ds-input-inner-wrapper"
                :class="[{ 'ds-input-force-focus-outline': forceFocusOutline }, inputInnerWrapperClasses]"
            >
                <div
                    v-if="$slots.leading"
                    class="flex h-full flex-col justify-center pr-2"
                >
                    <slot name="leading"></slot>
                </div>
                <slot
                    name="input"
                    v-bind="inputSlotProps"
                    @update:modelValue="model = $event"
                >
                    <input
                        v-model="model"
                        :id
                        ref="inputElement"
                        :autofocus
                        class="input-field"
                        :class="{ 'has-error': hasError }"
                        data-testid="ds-text-input"
                        :disabled
                        :list
                        :maxlength
                        :name
                        :placeholder
                        :readonly
                        :tabindex
                        :type
                        @blur="isFocused = false"
                        @focus="isFocused = true"
                    />
                </slot>
                <div
                    v-if="$slots.trailing"
                    class="flex h-full flex-col justify-center pl-2"
                >
                    <slot name="trailing"></slot>
                </div>
            </div>
        </label>
        <slot
            v-if="!hasError && !hasWarning && $slots.helperText"
            name="helperText"
        />
        <HelperMessage
            v-else
            :actionableHelperText
            :error
            :helperText
            :size
            :warning
            @clickHelperText="emit('clickHelperText')"
        />
    </div>
</template>

<script setup lang="ts">
import type { HelperMessageEmits } from '@/components/design-system/utils/helper-message.types';
import DsInputLabel from '@ds/form/input/DsInputLabel/DsInputLabel.vue';
import type { DsTextInputProps, DsTextInputSlots } from '@ds/form/input/text/text-input.types';
import HelperMessage from '@ds/utils/HelperMessage.vue';
import { computed, onMounted, useId, useTemplateRef, watch } from 'vue';

const model = defineModel();

defineSlots<DsTextInputSlots>();

const props = withDefaults(defineProps<DsTextInputProps>(), {
    autofocus: false,
    id: () => useId(),
    label: '',
    placeholder: '',
    helperText: '',
    error: false,
    warning: false,
    actionableHelperText: false,
    size: 'md',
    forceFocusOutline: false,
    inputInnerWrapperClasses: '',
    tabindex: 0,
});

const emit = defineEmits<HelperMessageEmits>();

const inputSlotProps = computed(() => ({
    ...props,
    elementId: props.id,
    inputClass: 'input-field',
    hasError: hasError.value,
}));

const inputElement = useTemplateRef('inputElement');

const hasError = computed(() => !!props.error);
const errorMessage = computed(() => {
    if (typeof props.error === 'string') {
        return props.error;
    }
    return '';
});

const hasWarning = computed(() => !hasError.value && !!props.warning);

const refreshInputValidity = (hasError: boolean) => {
    inputElement.value?.setCustomValidity(hasError ? errorMessage.value || 'Error' : '');
};

const isFocused = defineModel<boolean>('isFocused');
watch(isFocused, value => {
    if (value) {
        inputElement.value?.focus();
    }
});

watch(hasError, refreshInputValidity);
onMounted(() => {
    refreshInputValidity(hasError.value);
});

defineExpose({
    inputElement,
});
</script>

<style scoped lang="scss">
.ds-rounded-input {
    @apply rounded;
}

/** We want to avoid having the state colors apply to the inner input element, which is visually neutral */
.ds-text-input {
    @extend .ds-rounded-input;
    @apply mb-0 w-full bg-primary p-0 outline-transparent;

    &:has(:active),
    &:focus-within,
    &:has(.ds-input-force-focus-outline) {
        @apply shadow-outline-field;
        outline: 2px solid theme('colors.ds.gray.800');
    }

    &.has-warning {
        outline: 2px solid theme('colors.ds.yellow.400');
    }

    &:has(:invalid),
    &:has(.has-error) {
        outline: 2px solid theme('colors.ds.red.600');
    }

    &:has(.input-field:disabled),
    :disabled {
        @apply cursor-not-allowed bg-ds-gray-100 text-content-disabled;

        & .ds-input-inner-wrapper {
            @apply hover:bg-ds-gray-100;
        }
    }

    & .ds-input-inner-wrapper {
        @extend .ds-rounded-input;
        @apply flex w-full items-center leading-none text-content-primary outline outline-1 -outline-offset-[1px] outline-ds-primary-400 hover:bg-secondary;
    }

    &:has(:active),
    &:focus-within,
    &:has(.ds-input-force-focus-outline),
    &.has-warning,
    &:has(:invalid),
    &:has(.has-error) {
        & > .ds-input-inner-wrapper {
            @apply outline-transparent;
        }
    }

    :deep(.input-field) {
        @apply m-0 h-5 flex-1 border-none bg-transparent p-0 placeholder:text-content-secondary focus:ring-0;
    }
}

.ds-text-input-size-md {
    .ds-input-inner-wrapper {
        @apply h-10 p-3 text-content-lg;
    }
}

.ds-text-input-size-sm {
    .ds-input-inner-wrapper {
        @apply h-9 px-3 py-1.5 text-content-md;
    }

    :deep(.input-field) {
        @apply text-content-md;
    }
}
</style>
